import { blue, green, purple, red } from '@mui/material/colors';
import { STATUS_COLORS, STATUS_TEXT, kilobytes, megabytes } from '../constant';
import { Status } from '../types';

type ColorsMUI = 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | undefined;

type StatusColor = {
  color: string;
  backgroundColor: string;
  type: ColorsMUI
}

const colors: Record<string, StatusColor> = {
  default: { color: 'text.secondary', backgroundColor: '', type: 'default' },
  primary: { color: 'primary.main', backgroundColor: purple[50], type: 'primary' },
  error: { color: 'error.main', backgroundColor: red[50], type: 'error' },
  success: { color: 'success.main', backgroundColor: green[50], type: 'success' },
  info: { color: 'info.main', backgroundColor: blue[50], type: 'info' },
};

export const getColors = (status: Status) => {
  if (STATUS_COLORS.PRIMARY?.includes(status)) {
    return colors.primary;
  }

  if (STATUS_COLORS.ERROR?.includes(status)) {
    return colors.error;
  }

  if (STATUS_COLORS.SUCCESS?.includes(status)) {
    return colors.success;
  }

  if (STATUS_COLORS.INFO?.includes(status)) {
    return colors.info;
  }

  if (STATUS_COLORS.DEFAULT?.includes(status)) {
    return colors.default;
  }

  return colors.default;
};

export const mapOrderStatus = (status: Status) => {
  const statusText = STATUS_TEXT[status] || formatStatus(status) || 'OTHER';
  const statusColors = getColors(status);
  return { statusText, statusColors };
};

export const transformCurrency = ({
  number,
  currency = 'USD',
}: {
  number: number;
  currency?: string;
}) => {
  return (number || 0).toLocaleString('en-US', {
    style: 'currency',
    currency,
  });
};

export function formatDate(date: string & Date) {
  const now = new Date();
  const diff = (now.getTime() - date.getTime()) / 1000;
  const dayDiff = Math.floor(diff / 86400);

  if (isNaN(dayDiff) || dayDiff < 0 || dayDiff >= 31) {
    return date.toLocaleDateString();
  }

  if (dayDiff === 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;
    return `Today at ${hour12}:${minutesFormatted} ${ampm}`;
  }

  if (dayDiff === 1) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;
    return `Yesterday at ${hour12}:${minutesFormatted} ${ampm}`;
  }

  if (dayDiff < 7) {
    const weekday = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ][date.getDay()];
    return `${weekday} at ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  return date.toLocaleDateString();
}

export function formatBytes(bytes: number): string {
  if (bytes < kilobytes) {
    return bytes + ' B';
  }

  if (bytes < megabytes) {
    return (bytes / kilobytes).toFixed(2) + ' KB';
  }

  return (bytes / megabytes).toFixed(2) + ' MB';
}

export function isImage(file: File): boolean {
  const acceptedImageTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/svg+xml',
  ];

  return acceptedImageTypes.includes(file.type);
}

export function formatStatus(status = '') {
  return status.toUpperCase().replace(/_/g, ' ');
}
